import React, { useState } from 'react';
import { Box, Card, CardContent, CardHeader, Collapse, Container, IconButton, Stack, Typography } from '@mui/material';
import { AccountCircleOutlined, ExpandMore as ExpandMoreIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { IAudition, IRole } from '../../types/global/helper';
import CustomButton from '../Button/CustomButton';
import { useDispatch } from 'react-redux';
import { setRole, deleteRole } from '../../redux/audition/actions';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { useAuth } from '../../hook/useAuth';
import { toggleAuthModal } from '../../redux/authentication/action';
import { RichTextViewer } from '../Common/RichTextViewer';

const useStyles = makeStyles(() => ({
    root: {},
    expand: {
        transform: 'rotate(0deg)',
        marginLeft: 'auto',
        transition: `transform 100ms`,
    },
    expandOpen: {
        transform: 'rotate(180deg)',
        color: '#fff',
    },
    cardHeader: {
        // Style for the CardHeader
        backgroundColor: 'rbga(0,0,0,0.9)',
        color: '#fff',
        fontFamily: FONT_ENUM.REGULAR_HEADER,
        fontSize: 8,
    },
    cardContent: {
        // Style for the CardContent
        backgroundColor: 'rbga(0,0,0,0.9)',
        color: '#fff',
        fontFamily: FONT_ENUM.REGULAR_HEADER,
    },
}));

export interface IRoleRenderer {
    audition: IAudition;
    onApplyClick?: (role: IRole) => void;
    isManageMode?: boolean;
    onManageClick?: (role: IRole) => void;
}

const RoleRenderer: React.FC<IRoleRenderer> = ({ audition, onApplyClick, isManageMode, onManageClick }) => {
    const { roles: auditionRoles } = audition;
    const roles = auditionRoles.filter(role => role !== null);
    const classes = useStyles();
    const [selectedRole, setSelectedRole] = useState<IRole | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<IRole | null>(null);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const { isSignedIn } = useAuth();

    const handleDeleteClick = (role: IRole) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (roleToDelete?._id) {
            dispatch(deleteRole(roleToDelete._id));
        }
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };

    const handleExpandClick = (role: IRole) => {
        if (role._id === selectedRole?._id) {
            setSelectedRole(undefined);
        } else {
            setSelectedRole(role);
        }
    };

    const styles = { position: 'relative', width: '90%', m: 2 };

    const handleApplyClick = (role: IRole) => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        } else {
            if (isManageMode) {
                onManageClick && onManageClick(role);
            } else {
                dispatch(setRole(role)); //TODO: Move this to a central location
                onApplyClick && onApplyClick(role);
            }
        }
    };

    const SingleRoleRenderer = ({
        role,
        onClick,
        isManageMode,
    }: {
        role: IRole;
        onClick: (role: IRole) => void;
        isManageMode?: boolean;
    }) => {
        const expanded = role._id === selectedRole?._id;

        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    height: '100%',
                    pb: 0.5,
                }}>
                <Card
                    sx={{
                        width: '100%',
                    }}
                    style={{
                        marginBottom: 2,
                        backgroundColor: theme[themeStyle].secondaryBg,
                        border: `0.3px solid ${theme[themeStyle].highlight}`,
                    }}>
                    <CardHeader
                        title={
                            role.role_type === 'cast'
                                ? `${role.character} - ${role.gender} (${role.min_age}-${role.max_age})`
                                : role.character
                        }
                        className={classes.cardHeader}
                        titleTypographyProps={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: theme[themeStyle].tag_white,
                            textTransform: 'capitalize',
                        }}
                        action={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {isManageMode && (
                                    <IconButton
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleDeleteClick(role);
                                        }}
                                        sx={{
                                            color: theme[themeStyle].primary_red,
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                            },
                                        }}>
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                                <IconButton
                                    className={`${classes.expand} ${expanded ? classes.expandOpen : ''}`}
                                    onClick={() => handleExpandClick(role)}
                                    aria-expanded={expanded}
                                    aria-label="Show more">
                                    <ExpandMoreIcon sx={{ color: theme[themeStyle].highlight }} />
                                </IconButton>
                            </Box>
                        }
                    />
                    <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ position: 'relative' }}>
                        <CardContent className={classes.cardContent}>
                            <RichTextViewer
                                content={role.description}
                                textStyle={{ padding: 0, fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM }}
                            />
                            {role.role_type === 'cast' && (
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    → {role.min_age} - {role.max_age}
                                </Typography>
                            )}
                            {role.role_type === 'cast' && (
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        color: theme[themeStyle].tag_white,
                                        textTransform: 'capitalize',
                                    }}>
                                    → {role.gender}
                                </Typography>
                            )}
                            {role.nudity_or_kissing && (
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        color: theme[themeStyle].primary_red,
                                        fontWeight: 'bold',
                                    }}>
                                    → Contains nudity or kissing
                                </Typography>
                            )}
                        </CardContent>
                        <Container sx={{ width: '100%', alignItems: 'flex-end' }}>
                            <CustomButton
                                label={isManageMode ? 'Edit Role' : 'Apply'}
                                sx={{ ...(styles as any) }}
                                onClick={() => onClick && onClick(role)}
                            />
                        </Container>
                    </Collapse>
                </Card>
            </Box>
        );
    };

    return (
        <Container disableGutters maxWidth={false}>
            <Stack direction="row" gap={1} mb={2}>
                <AccountCircleOutlined
                    sx={{
                        color: theme[themeStyle].tag_white,
                    }}
                />
                <Typography
                    style={{
                        fontWeight: 'bold',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 18,
                        color: theme[themeStyle].tag_white,
                    }}>
                    Roles in this call
                </Typography>
            </Stack>
            {roles &&
                roles.map((role, index) => (
                    <SingleRoleRenderer
                        key={index}
                        role={role}
                        isManageMode={isManageMode}
                        onClick={handleApplyClick}
                    />
                ))}
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setRoleToDelete(null);
                }}
                onAgreeClicked={handleConfirmDelete}
                onDisagreeClick={() => {
                    setDeleteDialogOpen(false);
                    setRoleToDelete(null);
                }}
                header="Delete Role"
                body={`Are you sure you want to delete the role "${roleToDelete?.character}"? This action cannot be undone.`}
                positiveText="Delete"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: theme[themeStyle].tag_white,
                }}
            />
        </Container>
    );
};

export default RoleRenderer;
